"use client";

import { useState } from "react";
import { Plus, Cpu, Layers, Wrench, CircleCheck as CheckCircle2 } from 'lucide-react';
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function () {
  const [activeStage, setActiveStage] = useState(0);
  const stages = [{
    number: "01",
    title: "MARKET ANALYSIS",
    description: "We conduct deep-dive research into regional trends and buyer personas. Every listing is optimized with data-driven pricing and cross-border transaction feasibility studies to ensure maximum asset value.",
    metric: "Global Reach Strategy",
    icon: Cpu,
    diagram: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660969/img/market_analysis_commercialization.png",
    diagramAlt: "Real estate market data and trend analysis diagram"
  }, {
    number: "02",
    title: "ASSET POSITIONING",
    description: "Strategic staging and high-end architectural photography. We craft a narrative for your property that resonates with elite investors, utilizing our exclusive network of high-net-worth contacts.",
    metric: "Premium Visual Assets",
    icon: Layers,
    diagram: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660969/img/asset_positioning_luxury.webp",
    diagramAlt: "Luxury property positioning and branding diagram"
  }, {
    number: "03",
    title: "NEGOTIATION & LEGAL",
    description: "Expert representation through the closing process. We manage PROFECO compliance and SAT registration requirements, ensuring a transparent and legally sound transfer for all parties involved.",
    metric: "100% Compliance Rate",
    icon: Wrench,
    diagram: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660969/img/legal_negotiation_process.png",
    diagramAlt: "Legal compliance and transaction flow diagram"
  }, {
    number: "04",
    title: "POST-SALE ADVISORY",
    description: "Comprehensive handoff and portfolio management. We provide ongoing reporting and reinvestment strategies, maintaining our commitment to your long-term financial growth in the Cabo market.",
    metric: "Long-term Yield Focus",
    icon: CheckCircle2,
    diagram: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660969/img/post_sale_advisory_reporting.png",
    diagramAlt: "Investor reporting and portfolio growth diagram"
  }];
  const certifications = ["PROFECO COMPLIANT", "SAT REGISTERED", "14 YRS EXPERTISE"];
  const {
    headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const activeDiagram = stages[activeStage];
  return <section data-section-id="4628" id="strategic-commercialization" className="bg-background text-foreground py-24 md:py-40 px-4 sm:px-6 lg:px-10 relative overflow-hidden">
      {}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
      backgroundImage: "linear-gradient(var(--foreground) 1px, transparent 1px), linear-gradient(90deg, var(--foreground) 1px, transparent 1px)",
      backgroundSize: "120px 120px"
    }} />

      {}
      <div className="absolute top-6 left-6 font-mono text-[10px] text-slate-muted tracking-widest hidden md:block">
        [LAT: 22.8905 / LON: -109.9167]
      </div>
      <div className="absolute top-6 right-6 font-mono text-[10px] text-slate-muted tracking-widest hidden md:block">
        IMI_ELITE_V.01
      </div>

      <div className="max-w-7xl mx-auto relative">
        {}
        <div ref={headerRef} className="mb-16 md:mb-24 transition-all duration-700 opacity-100 translate-y-0">
          <div className="text-center mb-10 md:mb-14">
            <span className="font-mono text-xs md:text-sm text-primary tracking-[0.25em] inline-flex items-center gap-2">
              <span className="w-8 h-px bg-primary" />
              {"// SECTION_05 — COMMERCIALIZATION"}
              <span className="w-8 h-px bg-primary" />
            </span>
          </div>

          <h2 className="font-default text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold tracking-tight leading-[0.95] max-w-5xl">
            Strategic sales.
            <br />
            <span className="text-slate-muted">Elite results.</span>
          </h2>

          <div className="mt-8 flex items-center gap-4 font-mono text-xs text-slate-muted">
            <span className="w-12 h-px bg-border" />
            <span>PRECISION REAL ESTATE TRANSACTION WORKFLOW</span>
          </div>
        </div>

        {}
        <div ref={contentRef} className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-10 transition-all duration-700 delay-150 opacity-100 translate-y-0">
          {}
          <div className="lg:col-span-3 order-1">
            <div className="bg-card border border-border rounded-md relative aspect-square lg:aspect-auto lg:h-full min-h-[400px] overflow-hidden">
              {}
              <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-5 py-3 border-b border-border bg-background/60 backdrop-blur-sm z-10">
                <span className="font-mono text-[10px] text-primary tracking-widest">
                  STRATEGY_{activeDiagram.number}.PDF
                </span>
                <span className="font-mono text-[10px] text-slate-muted tracking-widest">
                  {activeDiagram.title}
                </span>
              </div>

              {}
              <div className="absolute top-12 left-4 w-3 h-3 border-l border-t border-primary/30" />
              <div className="absolute top-12 right-4 w-3 h-3 border-r border-t border-primary/30" />
              <div className="absolute bottom-12 left-4 w-3 h-3 border-l border-b border-primary/30" />
              <div className="absolute bottom-12 right-4 w-3 h-3 border-r border-b border-primary/30" />

              {}
              <div className="absolute inset-0 flex items-center justify-center p-8 pt-16 pb-16">
                <img key={activeDiagram.diagram} src={activeDiagram.diagram} data-wvc-srcset={`${activeDiagram.diagram}-wvc-srcset`} data-wvc-sizes={`${activeDiagram.diagram}-wvc-sizes`} alt={activeDiagram.diagramAlt} loading="lazy" decoding="async" className="w-full h-full object-contain opacity-90 transition-opacity duration-500 grayscale hover:grayscale-0" />
              </div>

              {}
              <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-5 py-3 border-t border-border bg-background/60 backdrop-blur-sm">
                <span className="font-mono text-[10px] text-slate-muted tracking-widest">
                  PHASE {activeDiagram.number} / 04
                </span>
                <span className="font-mono text-[10px] text-primary tracking-widest">
                  {activeDiagram.metric}
                </span>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-2 order-2 relative">
            {}
            <div className="absolute left-3 top-6 bottom-6 w-px bg-border hidden md:block" />

            <div className="flex flex-col">
              {stages.map((stage, i) => {
              const isActive = activeStage === i;
              const Icon = stage.icon;
              return <div key={stage.number} data-index={i} className={`relative border-b border-border last:border-b-0 transition-all duration-300 ${isActive ? "border-l-[3px] border-l-primary pl-5" : "border-l-[3px] border-l-transparent pl-5"}`}>
                    {}
                    <div className={`absolute -left-[7px] top-7 w-3 h-3 rounded-md border transition-all duration-300 hidden md:block ${isActive ? "bg-primary border-primary" : "bg-background border-border"}`} />

                    <button onClick={() => setActiveStage(i)} className="w-full text-left py-6 flex items-start justify-between gap-4 group cursor-pointer" aria-expanded={isActive}>
                      <div className="flex items-baseline gap-4 flex-1 min-w-0">
                        <span className={`font-mono text-sm tracking-widest shrink-0 transition-colors ${isActive ? "text-primary" : "text-slate-muted"}`}>
                          {stage.number} /
                        </span>
                        <h3 className={`font-default text-xl md:text-2xl font-semibold tracking-tight transition-colors ${isActive ? "text-foreground" : "text-foreground/60 group-hover:text-foreground"}`}>
                          {stage.title}
                        </h3>
                      </div>
                      <Plus className={`size-4 shrink-0 mt-2 transition-all duration-300 ${isActive ? "rotate-45 text-primary" : "text-slate-muted"}`} />
                    </button>

                    {}
                    <div className={`grid transition-all duration-500 ease-in-out ${isActive ? "grid-rows-[1fr] opacity-100 pb-6" : "grid-rows-[0fr] opacity-0"}`}>
                      <div className="overflow-hidden">
                        <div className="pl-0 md:pl-[3.25rem] space-y-4">
                          <p className="text-sm text-slate-muted leading-relaxed font-default">
                            {stage.description}
                          </p>
                          <div className="flex items-center gap-3 pt-2">
                            <Icon className="size-4 text-primary" />
                            <span className="font-mono text-xs text-primary tracking-wider">
                              {stage.metric}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>;
            })}
            </div>
          </div>
        </div>

        {}
        <div className="mt-16 md:mt-20 pt-8 border-t border-border">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <span className="font-mono text-[10px] text-slate-muted tracking-widest">
              {"// INSTITUTIONAL COMPLIANCE"}
            </span>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
              {certifications.map((cert, i) => <span key={cert} data-index={i} className="font-mono text-xs text-foreground tracking-widest flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-primary rounded-md" />
                  {cert}
                </span>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}